import { useState, useRef, useEffect, useCallback } from 'react';
import { BingoItem } from '@bst-stellar/shared';

interface ClaimModalProps {
    isOpen: boolean;
    onClose: () => void;
    item: BingoItem | null;
    position: { row: number; col: number } | null;
    cardId: string;
    existingClaim?: any; // Existing claim data for viewing/resubmission
    onSubmit: (data: {
        cardId: string;
        itemId: string;
        position: { row: number; col: number };
        description: string;
        evidenceUrls: string[];
        imageUrls: string[];
    }) => Promise<void>;
    onDelete?: (claimId: string) => Promise<void>; // For canceling pending claims
    onUploadFile?: (file: File) => Promise<{ url: string; key: string }>; // For file uploads
}

export default function ClaimModal({
    isOpen,
    onClose,
    item,
    position,
    cardId,
    existingClaim,
    onSubmit,
    onDelete,
    onUploadFile,
}: ClaimModalProps) {
    const [description, setDescription] = useState('');
    const [referenceUrl, setReferenceUrl] = useState('');
    const [referenceUrls, setReferenceUrls] = useState<string[]>([]);
    const [imageUrls, setImageUrls] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    // Reset form when modal opens or existingClaim changes
    // Only prefill form when resubmitting a rejected claim
    useEffect(() => {
        if (isOpen) {
            if (existingClaim?.status === 'rejected') {
                // Prefill form for resubmission of rejected claims
                setDescription(existingClaim.description || '');
                setReferenceUrls(existingClaim.evidenceUrls || []);
                setImageUrls(existingClaim.imageUrls || []);
            } else {
                // Clear form for new claims or viewing existing claims
                setDescription('');
                setReferenceUrls([]);
                setImageUrls([]);
            }
            setReferenceUrl('');
            setError('');
        }
    }, [isOpen, existingClaim]);

    // --- All hooks must be above the early return ---

    const uploadFile = useCallback(async (file: File) => {
        if (imageUrls.length >= 3) {
            setError('Maximum 3 images allowed');
            return;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            setError('Image must be less than 5MB');
            return;
        }

        setError('');
        setUploading(true);

        try {
            if (onUploadFile) {
                // Use cloud storage upload
                const result = await onUploadFile(file);
                setImageUrls(prev => [...prev, result.url]);
            } else {
                // Fallback: Create a local object URL (temporary preview only)
                const url = URL.createObjectURL(file);
                setImageUrls(prev => [...prev, url]);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to upload image');
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [imageUrls, onUploadFile]);

    // Global paste listener so user doesn't need to focus the drop zone
    useEffect(() => {
        if (!isOpen) return;
        function onGlobalPaste(e: ClipboardEvent) {
            // Don't intercept pastes inside text inputs / textareas
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
            const items = e.clipboardData?.items as DataTransferItemList | undefined;
            if (!items) return;
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.startsWith('image/')) {
                    const file = items[i].getAsFile();
                    if (file) {
                        uploadFile(file);
                        break;
                    }
                }
            }
        }
        document.addEventListener('paste', onGlobalPaste);
        return () => document.removeEventListener('paste', onGlobalPaste);
    }, [isOpen, uploadFile]);

    // --- Early return guard (no hooks below this line) ---
    if (!isOpen || !item || !position) return null;

    function addReferenceUrl() {
        if (referenceUrl.trim()) {
            setReferenceUrls([...referenceUrls, referenceUrl.trim()]);
            setReferenceUrl('');
        }
    }

    function removeReferenceUrl(index: number) {
        setReferenceUrls(referenceUrls.filter((_, i) => i !== index));
    }

    async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;
        await uploadFile(file);
    }

    function handleDrop(e: React.DragEvent) {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) uploadFile(file);
    }

    function handleImageAreaPaste(e: React.ClipboardEvent) {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.startsWith('image/')) {
                const file = items[i].getAsFile();
                if (file) {
                    uploadFile(file);
                    break;
                }
            }
        }
    }

    function removeImageUrl(index: number) {
        setImageUrls(imageUrls.filter((_, i) => i !== index));
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Auto-add any pending reference URL
            const finalReferenceUrls = referenceUrl.trim()
                ? [...referenceUrls, referenceUrl.trim()]
                : referenceUrls;

            // Always create a new claim (even for resubmissions)
            // This ensures rejected claims turn yellow when resubmitted
            await onSubmit({
                cardId,
                itemId: item!.id,
                position: position!,
                description,
                evidenceUrls: finalReferenceUrls,
                imageUrls,
            });

            // Reset form
            setDescription('');
            setReferenceUrls([]);
            setReferenceUrl('');
            setImageUrls([]);
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to submit claim');
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete() {
        if (!existingClaim || !onDelete) return;

        if (!confirm('Are you sure you want to cancel this claim? You can resubmit it later.')) {
            return;
        }

        try {
            setDeleting(true);
            await onDelete(existingClaim.id);
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to cancel claim');
        } finally {
            setDeleting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-border">
                <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                        <h2 className="text-2xl font-bold text-text-primary">
                            {existingClaim ? 'View Claim' : 'Submit Claim'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-text-secondary hover:text-text-primary"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="mb-6 p-4 bg-background rounded-lg border border-border">
                        <h3 className="font-semibold text-text-primary mb-2">{item.title}</h3>
                        <p className="text-text-secondary text-sm">{item.description}</p>
                        <div className="mt-2 flex gap-2">
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full border border-blue-500/30">
                                {item.category}
                            </span>
                            {item.isAiRelated && (
                                <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full border border-purple-500/30">
                                    AI-Related
                                </span>
                            )}
                            <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-full border border-gray-500/30 capitalize">
                                {item.difficulty}
                            </span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {error && (
                            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded">
                                {error}
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Description / Proof
                            </label>
                            <textarea
                                required
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface text-text-primary"
                                rows={4}
                                placeholder="Describe how you completed this item..."
                            />
                        </div>

                        {/* Reference Image Section - File Upload */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                📷 Reference Image (up to 3)
                            </label>
                            {imageUrls.length < 3 && (
                                <div
                                    className={`mb-2 border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                                        isDragging
                                            ? 'border-primary bg-primary/5'
                                            : 'border-border hover:border-primary hover:bg-surface'
                                    } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                                    onClick={() => fileInputRef.current?.click()}
                                    onDrop={handleDrop}
                                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                    onDragLeave={() => setIsDragging(false)}
                                    onPaste={handleImageAreaPaste}
                                    tabIndex={0}
                                >
                                    <div className="text-2xl mb-1">📎</div>
                                    <p className="text-sm text-text-secondary">
                                        <span className="font-semibold text-primary">Click to upload</span>, drag &amp; drop,
                                    </p>
                                    <p className="text-sm text-text-secondary">
                                        or <span className="font-semibold text-primary">paste</span> a screenshot (⌘V / Ctrl+V)
                                    </p>
                                    {uploading && (
                                        <p className="text-xs text-primary mt-1">Uploading...</p>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />
                                </div>
                            )}
                            {imageUrls.length > 0 && (
                                <div className="grid grid-cols-3 gap-2">
                                    {imageUrls.map((url, index) => (
                                        <div key={index} className="relative group">
                                            <img
                                                src={url}
                                                alt={`Reference ${index + 1}`}
                                                className="w-full h-24 object-cover rounded-lg border border-border"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23333" width="100" height="100"/><text fill="%23999" font-size="12" x="50%" y="50%" text-anchor="middle">Invalid</text></svg>';
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeImageUrl(index)}
                                                className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="text-xs text-text-muted mt-1">
                                {imageUrls.length}/3 images added • Max 5MB per image
                            </p>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                🔗 Reference URL (Optional)
                            </label>
                            <p className="text-xs text-text-muted mb-2">
                                e.g., PR link, Slack message, Jira ticket, etc.
                            </p>
                            <div className="flex gap-2 mb-2">
                                <input
                                    type="url"
                                    value={referenceUrl}
                                    onChange={(e) => setReferenceUrl(e.target.value)}
                                    className="flex-1 px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface text-text-primary"
                                    placeholder="https://..."
                                />
                                <button
                                    type="button"
                                    onClick={addReferenceUrl}
                                    className="px-4 py-2 bg-background text-text-secondary rounded-lg hover:bg-border border border-border"
                                >
                                    Add
                                </button>
                            </div>
                            {referenceUrls.length > 0 && (
                                <ul className="space-y-1">
                                    {referenceUrls.map((url: string, index: number) => (
                                        <li
                                            key={index}
                                            className="flex items-center gap-2 text-sm bg-background p-2 rounded border border-border"
                                        >
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex-1 text-primary hover:underline truncate"
                                            >
                                                {url}
                                            </a>
                                            <button
                                                type="button"
                                                onClick={() => removeReferenceUrl(index)}
                                                className="text-red-400 hover:text-red-300"
                                            >
                                                Remove
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="flex gap-3 justify-end">
                            {existingClaim?.status === 'pending' && onDelete && (
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    disabled={deleting || loading}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                                >
                                    {deleting ? 'Canceling...' : 'Cancel Claim'}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border border-border rounded-md text-text-secondary hover:bg-background"
                            >
                                Close
                            </button>
                            {existingClaim?.status !== 'approved' && (
                                <button
                                    type="submit"
                                    disabled={loading || deleting || !description.trim()}
                                    className="btn-primary"
                                >
                                    {loading ? 'Submitting...' : existingClaim ? 'Resubmit Claim' : 'Submit Claim'}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
